import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import {
    adminUsersApi,
    membershipsApi,
    userProfileApi,
    organizationsApi,
    partnersApi,
} from '../lib/api';
import { Header } from '../components/Header';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { ChangePasswordSection } from '../components/ChangePasswordSection';
import { ProfilePictureDialog } from '../components/ProfilePictureDialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { LANGUAGE_NAMES } from '../lib/languageNames';
import { toast } from 'sonner';
import {
    ArrowLeft,
    User,
    Mail,
    Calendar,
    Hash,
    Clock,
    Building2,
    Bell,
    Plus,
    Star,
    X,
    Loader2,
    Search,
    Shield,
    AlertTriangle,
    Save,
    KeyRound,
    Send,
    Camera,
} from 'lucide-react';

// Notification keys (must mirror backend NOTIFICATION_KEYS)
const NOTIFICATION_DEFINITIONS = [
    { key: 'production_confirmed', label: 'Production confirmed', tKey: 'profile.notif.production_confirmed', help: 'When a supplier confirms an order is in production', helpKey: 'profile.notif.production_confirmed_help' },
    { key: 'delivery_notice', label: 'Shipping / delivery notice', tKey: 'profile.notif.delivery_notice', help: 'When goods are shipped from the supplier', helpKey: 'profile.notif.delivery_notice_help' },
    { key: 'invoice_email', label: 'Invoice', tKey: 'profile.notif.invoice_email', help: 'When a new invoice is issued', helpKey: 'profile.notif.invoice_email_help' },
    { key: 'artwork_revision_requested', label: 'Artwork revision requested', tKey: 'profile.notif.artwork_revision_requested', help: 'When the repro partner asks for an artwork revision', helpKey: 'profile.notif.artwork_revision_requested_help' },
    { key: 'artwork_ready_for_approval', label: 'Artwork ready for approval', tKey: 'profile.notif.artwork_ready_for_approval', help: 'When artwork has been corrected and is ready to review', helpKey: 'profile.notif.artwork_ready_for_approval_help' },
    { key: 'upload_link', label: 'Upload link', tKey: 'profile.notif.upload_link', help: 'When an upload link is shared with you', helpKey: 'profile.notif.upload_link_help' },
    { key: 'order_updates', label: 'Order updates', tKey: 'profile.notif.order_updates', help: 'Generic status changes on orders', helpKey: 'profile.notif.order_updates_help' },
];

// Mirrors backend MANDATORY_NOTIFICATION_KEYS — these are action-required
// transactional emails (customer must respond) and CANNOT be turned off.
const FALLBACK_MANDATORY_KEYS = ['artwork_revision_requested', 'artwork_ready_for_approval'];

const MEMBERSHIP_ROLES = [
    { value: 'owner', label: 'Owner', help: 'Full control. Can promote/demote and remove other users.' },
    { value: 'admin', label: 'Admin', help: 'Can manage orders, finance and most settings.' },
    { value: 'member', label: 'Member', help: 'Day-to-day user. Receives most notifications by default.' },
    { value: 'viewer', label: 'Viewer', help: 'Read-only. No notifications by default.' },
];

const ROLE_BADGE_STYLES = {
    owner: 'bg-amber-100 text-amber-800',
    admin: 'bg-indigo-100 text-indigo-700',
    member: 'bg-slate-100 text-slate-700',
    viewer: 'bg-slate-100 text-slate-500',
    customer: 'bg-slate-100 text-slate-700',
};

export default function UserDetailPage() {
    const { userId: paramUserId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser, setUser: setAuthUser } = useAuth();
    const { t, supportedLanguages, setLanguage } = useTranslation();
    // When mounted at `/profile` the param is missing; fall back to current user.
    const userId = paramUserId || currentUser?.id;

    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('profile');

    // Profile-tab state
    const [editForm, setEditForm] = useState({
        name: '',
        role: 'customer',
        is_active: true,
        partner_id: '',
        default_landing_page: '',
        preferred_language: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [reproPartners, setReproPartners] = useState([]);

    // Companies & Roles tab state
    const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false);
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const [companySearchResults, setCompanySearchResults] = useState([]);
    const [isSearchingCompanies, setIsSearchingCompanies] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [selectedMembershipRole, setSelectedMembershipRole] = useState('member');
    const [isAddingCompany, setIsAddingCompany] = useState(false);
    const [removingMembershipId, setRemovingMembershipId] = useState(null);
    const [updatingMembershipId, setUpdatingMembershipId] = useState(null);
    const [sendingReset, setSendingReset] = useState(false);

    // Notifications tab state — local edits keyed by company_id
    const [notifEdits, setNotifEdits] = useState({});
    const [savingNotifCompanyId, setSavingNotifCompanyId] = useState(null);

    // Profile picture dialog — only meaningful when the viewer is
    // looking at their own profile. Admins editing OTHER users don't
    // upload photos on behalf of them.
    const [showPictureDialog, setShowPictureDialog] = useState(false);

    const isCurrentUser = profile?.id === currentUser?.id;
    const isOwnerActor = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
    const isAdminActor = isOwnerActor;
    // Self-service mode: a non-admin viewing their own profile (via /profile route).
    // They can only edit their display name + their own notification prefs.
    const isSelfServiceMode = isCurrentUser && !isAdminActor;

    const fetchProfile = useCallback(async () => {
        try {
            const { data } = await userProfileApi.getProfile(userId);
            setProfile(data);
            setEditForm({
                name: data.name || '',
                role: data.role,
                is_active: data.is_active !== false,
                partner_id: data.partner_id || '',
                default_landing_page: data.default_landing_page || '',
                preferred_language: data.preferred_language || '',
            });
            setNotifEdits(data.notification_preferences || {});
        } catch (err) {
            console.error('Failed to load profile:', err);
            toast.error(err.response?.data?.detail || 'Failed to load user profile');
        } finally {
            setIsLoading(false);
        }
    }, [userId]);

    const fetchReproPartners = useCallback(async () => {
        try {
            const { data } = await partnersApi.list({ type: 'Repro', is_active: true });
            setReproPartners(data);
        } catch (err) {
            console.error('Failed to fetch repro partners:', err);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
        fetchReproPartners();
    }, [fetchProfile, fetchReproPartners]);

    // ==================== Profile Tab Actions ====================

    const handleSendPasswordReset = async () => {
        if (!profile?.email) return;
        if (!window.confirm(`Send a password reset email to ${profile.email}?`)) return;
        setSendingReset(true);
        try {
            const { data } = await userProfileApi.sendPasswordReset(profile.id);
            if (data?.success) {
                toast.success(
                    `Reset email sent to ${data.recipient_email} — link expires in ${data.expires_in_minutes} min.`
                );
            } else {
                toast.error('Reset email could not be sent — check SMTP integration.');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not send reset email');
        } finally {
            setSendingReset(false);
        }
    };

    const handleSaveProfile = async () => {
        if (!profile) return;
        setIsSaving(true);
        try {
            // Self-service mode: only `name` + `default_landing_page` are
            // editable; use /auth/me endpoint.
            if (isSelfServiceMode) {
                const payload = {};
                const trimmedName = (editForm.name || '').trim();
                if (trimmedName !== (profile.name || '').trim()) {
                    payload.name = trimmedName;
                }
                if ((editForm.default_landing_page || '') !== (profile.default_landing_page || '')) {
                    payload.default_landing_page = editForm.default_landing_page || '';
                }
                if ((editForm.preferred_language || '') !== (profile.preferred_language || '')) {
                    payload.preferred_language = editForm.preferred_language || '';
                }
                if (Object.keys(payload).length === 0) {
                    toast.info(t('profile.no_changes', 'No changes to save'));
                    return;
                }
                await userProfileApi.updateSelf(payload);
                // Immediately reflect language change in-app (no need to
                // wait for /auth/me refresh). setLanguage persists to LS
                // too — the server PATCH above is idempotent.
                if (payload.preferred_language) {
                    setLanguage(payload.preferred_language);
                }
                toast.success(t('profile.updated', 'Profile updated'));
                await fetchProfile();
                return;
            }

            const updateData = {};
            if (editForm.name !== profile.name) updateData.name = editForm.name;
            if (editForm.role !== profile.role) updateData.role = editForm.role;
            if (editForm.is_active !== (profile.is_active !== false)) {
                updateData.is_active = editForm.is_active;
            }
            const currentPartnerId = profile.partner_id || '';
            if (editForm.partner_id !== currentPartnerId) {
                updateData.partner_id = editForm.partner_id || null;
            }
            if (editForm.role !== 'repro_partner' && profile.partner_id) {
                updateData.partner_id = null;
            }
            // Own landing-page preference — admins editing OTHER users don't
            // change this (it's a personal setting). Only push it when the
            // admin IS editing their own profile.
            if (isCurrentUser
                && (editForm.default_landing_page || '') !== (profile.default_landing_page || '')) {
                await userProfileApi.updateSelf({
                    default_landing_page: editForm.default_landing_page || '',
                });
            }
            if (Object.keys(updateData).length === 0) {
                if (isCurrentUser
                    && (editForm.default_landing_page || '') !== (profile.default_landing_page || '')) {
                    toast.success(t('profile.updated', 'Profile updated'));
                    await fetchProfile();
                    return;
                }
                toast.info(t('profile.no_changes', 'No changes to save'));
                return;
            }
            await adminUsersApi.update(profile.id, updateData);
            toast.success(t('profile.updated', 'Profile updated'));
            await fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('profile.update_error', 'Failed to update profile'));
        } finally {
            setIsSaving(false);
        }
    };

    // ==================== Companies & Roles Actions ====================

    const searchCompanies = async (query) => {
        if (!profile) return;
        setIsSearchingCompanies(true);
        try {
            const { data } = await membershipsApi.searchCompaniesForUser(profile.id, query, true);
            setCompanySearchResults(data || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to search companies');
        } finally {
            setIsSearchingCompanies(false);
        }
    };

    const handleAddCompany = async () => {
        if (!profile || !selectedCompanyId) {
            toast.error('Please select a company');
            return;
        }
        setIsAddingCompany(true);
        try {
            await membershipsApi.create({
                user_id: profile.id,
                company_id: selectedCompanyId,
                role: selectedMembershipRole,
                is_primary: (profile.memberships || []).length === 0,
            });
            toast.success('Added user to company');
            setShowAddCompanyDialog(false);
            setSelectedCompanyId('');
            setCompanySearchQuery('');
            setCompanySearchResults([]);
            setSelectedMembershipRole('member');
            await fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add company');
        } finally {
            setIsAddingCompany(false);
        }
    };

    const handleRemoveMembership = async (m) => {
        if (!window.confirm(`Remove ${profile?.name} from ${m.company_name}?`)) return;
        setRemovingMembershipId(m.id);
        try {
            await membershipsApi.delete(m.id);
            toast.success(`Removed from ${m.company_name}`);
            await fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove');
        } finally {
            setRemovingMembershipId(null);
        }
    };

    const handleSetPrimary = async (m) => {
        setUpdatingMembershipId(m.id);
        try {
            await membershipsApi.update(m.id, { is_primary: true });
            toast.success(`${m.company_name} is now the primary company`);
            await fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update');
        } finally {
            setUpdatingMembershipId(null);
        }
    };

    const handleChangeMembershipRole = async (m, newRole) => {
        if (newRole === m.role) return;
        // Only owner-on-team or admin actor can promote to owner
        if (newRole === 'owner' && !isOwnerActor) {
            toast.error('Only an admin can promote a user to Owner');
            return;
        }
        setUpdatingMembershipId(m.id);
        try {
            await membershipsApi.update(m.id, { role: newRole });
            toast.success(`Role set to ${newRole}`);
            await fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to change role');
        } finally {
            setUpdatingMembershipId(null);
        }
    };

    // ==================== Notification Actions ====================

    const mandatoryKeys = useMemo(
        () => new Set(profile?.mandatory_notification_keys || FALLBACK_MANDATORY_KEYS),
        [profile]
    );

    // Per-(company, key) recipient counts from API. Used to detect when
    // *this* user is the LAST enabled recipient in their company for a
    // mandatory key — in which case the toggle must stay on.
    const recipientCounts = profile?.notification_recipient_counts || {};

    const isLastRecipient = (companyId, key) => {
        if (!mandatoryKeys.has(key)) return false;
        const original = profile?.notification_preferences?.[companyId] || {};
        const wasEnabled = Boolean(original[key]);
        if (!wasEnabled) return false;
        const count = (recipientCounts?.[companyId] || {})[key];
        return Number.isFinite(count) && count <= 1;
    };

    const handleToggleNotif = (companyId, key, value) => {
        // Block disabling the LAST recipient for a mandatory key
        if (mandatoryKeys.has(key) && value === false && isLastRecipient(companyId, key)) {
            toast.error(
                t(
                    'profile.mandatory_notif_error',
                    'At least one company member must receive artwork approval and revision emails. Ask another teammate to enable this first.'
                )
            );
            return;
        }
        setNotifEdits((prev) => ({
            ...prev,
            [companyId]: {
                ...(prev[companyId] || {}),
                [key]: value,
            },
        }));
    };

    const handleBulkToggle = (companyId, value) => {
        setNotifEdits((prev) => {
            const next = { ...(prev[companyId] || {}) };
            NOTIFICATION_DEFINITIONS.forEach(({ key }) => {
                // Don't bulk-disable a mandatory key if user is the last recipient
                if (value === false && mandatoryKeys.has(key) && isLastRecipient(companyId, key)) {
                    next[key] = true;
                } else {
                    next[key] = value;
                }
            });
            return { ...prev, [companyId]: next };
        });
    };

    const handleSaveNotifs = async (companyId) => {
        setSavingNotifCompanyId(companyId);
        try {
            const prefs = notifEdits[companyId] || {};
            await userProfileApi.updateNotifications(profile.id, companyId, prefs);
            toast.success(t('profile.notif_saved', 'Notification preferences saved'));
            // Refresh in case backend mutated keys
            await fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('profile.notif_save_error', 'Failed to save preferences'));
        } finally {
            setSavingNotifCompanyId(null);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const memberships = profile?.memberships || [];
    const hasUnsavedNotifChanges = useMemo(() => (companyId) => {
        const original = profile?.notification_preferences?.[companyId] || {};
        const edited = notifEdits[companyId] || {};
        return NOTIFICATION_DEFINITIONS.some(
            ({ key }) => Boolean(original[key]) !== Boolean(edited[key])
        );
    }, [profile, notifEdits]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center py-20">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="max-w-3xl mx-auto px-6 py-10 text-center">
                    <p className="text-slate-500">{t('profile.not_found', 'User not found.')}</p>
                    <Button onClick={() => navigate(isSelfServiceMode ? '/dashboard' : '/users')} className="mt-4" variant="outline">
                        {isSelfServiceMode ? t('profile.back_to_dashboard', 'Back to Dashboard') : 'Back to Users'}
                    </Button>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="max-w-5xl mx-auto px-6 py-6">
                {/* Header */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate(isSelfServiceMode ? '/dashboard' : '/users')}
                        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
                        data-testid="back-to-users-btn"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {isSelfServiceMode ? t('profile.back_to_dashboard', 'Back to Dashboard') : 'Back to Users'}
                    </button>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                            {(() => {
                                // Prefer the freshest picture: the AuthContext
                                // user for the current viewer (updates
                                // reactively after upload), otherwise fall
                                // back to whatever the profile API returned.
                                const effectivePicture = isCurrentUser
                                    ? (currentUser?.picture || profile.picture)
                                    : profile.picture;
                                const avatarContent = effectivePicture ? (
                                    <img
                                        src={effectivePicture}
                                        alt={profile.name || 'User'}
                                        className="w-14 h-14 rounded-full object-cover border border-slate-200"
                                        data-testid="profile-avatar-image"
                                    />
                                ) : (
                                    <div
                                        className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-xl font-semibold text-slate-600"
                                        data-testid="profile-avatar-initials"
                                    >
                                        {profile.name?.charAt(0).toUpperCase() || '?'}
                                    </div>
                                );
                                if (!isCurrentUser) return avatarContent;
                                return (
                                    <button
                                        type="button"
                                        onClick={() => setShowPictureDialog(true)}
                                        className="relative group focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded-full"
                                        title={t('profile.change_picture_title', 'Change profile picture')}
                                        aria-label={t('profile.change_picture_title', 'Change profile picture')}
                                        data-testid="change-profile-picture-btn"
                                    >
                                        {avatarContent}
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                                            <Camera className="h-5 w-5" />
                                        </span>
                                    </button>
                                );
                            })()}
                            <div>
                                <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                                    {profile.name}
                                </h1>
                                <p className="text-sm text-slate-500 flex items-center gap-2">
                                    <Mail className="h-3.5 w-3.5" />
                                    {profile.email}
                                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        profile.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                        {profile.is_active ? t('profile.active', 'Active') : t('profile.disabled', 'Disabled')}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="mb-6" data-testid="user-detail-tabs">
                        <TabsTrigger value="profile" data-testid="tab-profile">
                            <User className="h-4 w-4 mr-1.5" />
                            {t('profile.tab_profile', 'Profile')}
                        </TabsTrigger>
                        <TabsTrigger value="companies" data-testid="tab-companies">
                            <Building2 className="h-4 w-4 mr-1.5" />
                            {t('profile.tab_companies', 'Companies & Roles')}
                        </TabsTrigger>
                        <TabsTrigger value="notifications" data-testid="tab-notifications">
                            <Bell className="h-4 w-4 mr-1.5" />
                            {t('profile.tab_notifications', 'Email Notifications')}
                        </TabsTrigger>
                    </TabsList>

                    {/* ==================== Profile Tab ==================== */}
                    <TabsContent value="profile">
                        <div className="bg-white rounded-lg border border-border shadow-premium-sm p-6 space-y-6">
                            <div>
                                <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('profile.readonly_info', 'Read-only info')}</h3>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex items-center gap-2">
                                        <Hash className="h-4 w-4 text-slate-400" />
                                        <span className="text-slate-500">{t('profile.id_label', 'ID:')}</span>
                                        <span className="font-mono text-xs text-slate-700">{profile.id}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-slate-400" />
                                        <span className="text-slate-500">{t('profile.created_label', 'Created:')}</span>
                                        <span className="text-slate-700">{formatDateTime(profile.created_at)}</span>
                                    </div>
                                    <div className="flex items-center gap-2" data-testid="user-detail-last-login">
                                        <Clock className="h-4 w-4 text-slate-400" />
                                        <span className="text-slate-500">{t('profile.last_login_label', 'Last login:')}</span>
                                        <span className="text-slate-700">
                                            {profile.last_login
                                                ? formatDateTime(profile.last_login)
                                                : t('profile.last_login_never', 'Never')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-5 space-y-4">
                                <h3 className="text-sm font-semibold text-slate-700">{t('profile.editable', 'Editable')}</h3>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">{t('profile.display_name', 'Display Name')}</Label>
                                        <Input
                                            id="name"
                                            value={editForm.name}
                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            data-testid="edit-name-input"
                                        />
                                    </div>
                                    {isAdminActor && (
                                    <div className="space-y-2">
                                        <Label htmlFor="role">Global Role</Label>
                                        {isCurrentUser ? (
                                            <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                                                <AlertTriangle className="h-4 w-4" />
                                                <span>You cannot change your own role</span>
                                            </div>
                                        ) : (
                                            <Select
                                                value={editForm.role}
                                                onValueChange={(v) =>
                                                    setEditForm({
                                                        ...editForm,
                                                        role: v,
                                                        partner_id: v !== 'repro_partner' ? '' : editForm.partner_id,
                                                    })
                                                }
                                            >
                                                <SelectTrigger data-testid="edit-role-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="customer">Customer</SelectItem>
                                                    <SelectItem value="admin">Admin</SelectItem>
                                                    <SelectItem value="super_admin">Super Admin</SelectItem>
                                                    <SelectItem value="translator">Translator</SelectItem>
                                                    <SelectItem value="repro_partner">Repro Partner</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </div>
                                    )}
                                </div>

                                {isAdminActor && editForm.role === 'repro_partner' && !isCurrentUser && (
                                    <div className="space-y-2">
                                        <Label>Linked Repro Partner</Label>
                                        <Select
                                            value={editForm.partner_id}
                                            onValueChange={(v) => setEditForm({ ...editForm, partner_id: v })}
                                        >
                                            <SelectTrigger data-testid="edit-partner-select">
                                                <SelectValue placeholder="Select a repro partner..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {reproPartners.length === 0 ? (
                                                    <div className="px-2 py-3 text-sm text-slate-500 text-center">
                                                        No repro partners available.
                                                    </div>
                                                ) : (
                                                    reproPartners.map((p) => (
                                                        <SelectItem key={p.id} value={p.id}>
                                                            {p.name}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {isAdminActor && (
                                <div className="space-y-2">
                                    <Label>Account Status</Label>
                                    {isCurrentUser ? (
                                        <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                                            <AlertTriangle className="h-4 w-4" />
                                            <span>You cannot disable your own account</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-2 h-2 rounded-full ${editForm.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                                <span className="text-sm font-medium">
                                                    {editForm.is_active ? 'Active' : 'Disabled'}
                                                </span>
                                            </div>
                                            <Switch
                                                checked={editForm.is_active}
                                                onCheckedChange={(c) => setEditForm({ ...editForm, is_active: c })}
                                                data-testid="edit-status-switch"
                                            />
                                        </div>
                                    )}
                                </div>
                                )}

                                {/* Default landing page — only visible when the user is
                                    editing their OWN profile. Personal setting: an
                                    admin viewing another user's profile does not
                                    override where that user lands after login. */}
                                {isCurrentUser && (
                                    <div className="space-y-2">
                                        <Label htmlFor="default-landing-page">{t('profile.default_landing', 'Default landing page after login')}</Label>
                                        <Select
                                            value={editForm.default_landing_page || 'orders'}
                                            onValueChange={(v) =>
                                                setEditForm({ ...editForm, default_landing_page: v })
                                            }
                                        >
                                            <SelectTrigger id="default-landing-page" data-testid="edit-default-landing-page-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(editForm.role === 'admin' || editForm.role === 'super_admin') && (
                                                    <SelectItem value="overview">Overview</SelectItem>
                                                )}
                                                <SelectItem value="orders">{t('profile.landing_orders', 'Orders')}</SelectItem>
                                                <SelectItem value="artwork-jobs">{t('profile.landing_artwork', 'Artwork Jobs')}</SelectItem>
                                                {(editForm.role === 'admin' || editForm.role === 'super_admin') && (
                                                    <SelectItem value="finance">Finance</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-500">
                                            {t('profile.default_landing_hint', 'Choose which page opens right after you sign in.')}
                                        </p>
                                    </div>
                                )}

                                {isCurrentUser && (
                                    <div className="space-y-2">
                                        <Label htmlFor="preferred-language">{t('profile.preferred_language', 'Preferred language')}</Label>
                                        <Select
                                            value={editForm.preferred_language || ''}
                                            onValueChange={(v) =>
                                                setEditForm({ ...editForm, preferred_language: v === '__auto__' ? '' : v })
                                            }
                                        >
                                            <SelectTrigger id="preferred-language" data-testid="edit-preferred-language-select">
                                                <SelectValue placeholder={t('profile.preferred_language_auto', 'Automatic (use company / browser default)')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__auto__">
                                                    {t('profile.preferred_language_auto', 'Automatic (use company / browser default)')}
                                                </SelectItem>
                                                {supportedLanguages.map(lang => (
                                                    <SelectItem key={lang} value={lang} data-testid={`preferred-language-option-${lang}`}>
                                                        <span className="uppercase font-mono tracking-wide text-slate-500 mr-2">{lang}</span>
                                                        {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-500">
                                            {t('profile.preferred_language_hint', 'Sets the UI language across your devices. Overrides Company / Affinity / Campaign / Landing Page defaults.')}
                                        </p>
                                    </div>
                                )}

                                <div className="flex justify-end pt-3 border-t">
                                    <Button
                                        onClick={handleSaveProfile}
                                        disabled={isSaving}
                                        className="bg-indigo-600 hover:bg-indigo-700"
                                        data-testid="save-profile-btn"
                                    >
                                        {isSaving ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="h-4 w-4 mr-2" />
                                        )}
                                        {t('profile.save_button', 'Save Profile')}
                                    </Button>
                                </div>

                                {isAdminActor && !isCurrentUser && (
                                    <div className="border-t pt-5 mt-5">
                                        <h3 className="text-sm font-semibold text-slate-700 mb-1">
                                            Account actions
                                        </h3>
                                        <p className="text-xs text-slate-500 mb-4">
                                            Help this user regain access without
                                            sharing a password. They'll receive an
                                            email with a secure link valid for 45
                                            minutes.
                                        </p>
                                        <div className="flex items-center justify-between p-3 bg-amber-50/60 border border-amber-200 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <KeyRound className="h-4 w-4 text-amber-600" />
                                                <div>
                                                    <div className="text-sm font-medium text-amber-900">
                                                        Send password reset email
                                                    </div>
                                                    <div className="text-xs text-amber-800/80">
                                                        Sends to <code>{profile.email}</code>
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={handleSendPasswordReset}
                                                disabled={sendingReset || !profile.is_active}
                                                className="border-amber-300 text-amber-800 hover:bg-amber-100"
                                                data-testid="btn-admin-send-password-reset"
                                            >
                                                {sendingReset ? (
                                                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                                ) : (
                                                    <Send className="h-4 w-4 mr-1.5" />
                                                )}
                                                Send reset email
                                            </Button>
                                        </div>
                                        {!profile.is_active && (
                                            <p className="text-xs text-red-600 mt-2">
                                                User is disabled — re-enable them
                                                before sending a reset email.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Self-service password change — rendered
                                    whenever the caller is viewing their OWN
                                    profile (customers, admins, super-admins
                                    all included). Admins editing OTHER users
                                    still see the separate "Send password
                                    reset" flow below; this section is
                                    intentionally scoped to the self-edit
                                    case so an admin can't bypass the
                                    current-password check by swapping URL. */}
                                {isCurrentUser && <ChangePasswordSection />}
                            </div>
                        </div>
                    </TabsContent>

                    {/* ==================== Companies & Roles Tab ==================== */}
                    <TabsContent value="companies">
                        <div className="bg-white rounded-lg border border-border shadow-premium-sm p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-700">{t('profile.memberships_title', 'Company memberships')}</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {t('profile.memberships_desc', 'One per-company role. Role determines default email subscriptions.')}
                                    </p>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setShowAddCompanyDialog(true);
                                        searchCompanies('');
                                    }}
                                    data-testid="add-company-btn"
                                    disabled={!isAdminActor}
                                    title={!isAdminActor ? t('profile.add_company_admin_only', 'Only admins can add companies here') : ''}
                                >
                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                    {t('profile.add_company', 'Add Company')}
                                </Button>
                            </div>

                            {!isAdminActor && (
                                <div className="mb-3 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded p-2">
                                    {t('profile.leave_company_hint_prefix', 'To leave a company or change your role, ask a company owner from the ')}<button onClick={() => navigate('/team')} className="text-indigo-600 underline">{t('profile.team_link', 'Team')}</button>{t('profile.leave_company_hint_suffix', ' page.')}
                                </div>
                            )}

                            {memberships.length === 0 ? (
                                <div className="text-center py-8 text-sm text-slate-400 bg-background rounded-lg">
                                    {t('profile.no_memberships', 'No company memberships yet')}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {memberships.map((m) => (
                                        <div
                                            key={m.id}
                                            className="flex items-center justify-between gap-3 p-3 bg-background rounded-lg border"
                                            data-testid={`membership-row-${m.company_id}`}
                                        >
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                <Building2 className="h-5 w-5 text-slate-400 flex-shrink-0" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <button
                                                            className="font-medium text-slate-800 text-sm hover:text-indigo-600 hover:underline truncate"
                                                            onClick={() => navigate(`/admin/companies/${m.company_id}`)}
                                                        >
                                                            {m.company_name}
                                                        </button>
                                                        {m.is_primary && (
                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 inline-flex items-center gap-0.5">
                                                                <Star className="h-2.5 w-2.5" />
                                                                {t('profile.primary_badge', 'Primary')}
                                                            </span>
                                                        )}
                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${ROLE_BADGE_STYLES[m.role] || 'bg-slate-100 text-slate-600'}`}>
                                                            {m.role}
                                                        </span>
                                                    </div>
                                                    {/* Affinity Programme — company-based, only rendered when
                                                        the company is a member of an ACTIVE programme. Never
                                                        says "Logged in with X"; the programme belongs to the
                                                        company. Family discounts are read-only info. */}
                                                    {m.affinity_programme?.name && (
                                                        <div className="mt-1 text-[11px] text-emerald-700"
                                                            data-testid={`membership-affinity-${m.company_id}`}>
                                                            {t('profile.affinity_label', 'Affinity Programme:')}{' '}
                                                            <span className="font-medium">
                                                                {m.affinity_programme.name}
                                                            </span>
                                                            {Object.entries(m.affinity_programme.discount_by_family || {})
                                                                .filter(([, v]) => Number(v) > 0).length > 0 && (
                                                                    <span className="text-slate-500">
                                                                        {' · '}
                                                                        {Object.entries(m.affinity_programme.discount_by_family || {})
                                                                            .filter(([, v]) => Number(v) > 0)
                                                                            .map(([fam, pct]) => (
                                                                                <span key={fam} className="mr-2">
                                                                                    <span className="capitalize">{fam}</span>{' '}
                                                                                    <span className="font-medium">{pct}%</span>
                                                                                </span>
                                                                            ))}
                                                                    </span>
                                                                )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                {isAdminActor ? (
                                                <Select
                                                    value={m.role}
                                                    onValueChange={(v) => handleChangeMembershipRole(m, v)}
                                                    disabled={updatingMembershipId === m.id}
                                                >
                                                    <SelectTrigger className="w-[140px] h-8" data-testid={`role-select-${m.company_id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {MEMBERSHIP_ROLES.map((r) => (
                                                            <SelectItem
                                                                key={r.value}
                                                                value={r.value}
                                                                disabled={r.value === 'owner' && !isOwnerActor}
                                                            >
                                                                {r.label}
                                                            </SelectItem>
                                                        ))}
                                                        {/* Allow keeping legacy customer role if currently set */}
                                                        {m.role === 'customer' && (
                                                            <SelectItem value="customer">Customer (legacy)</SelectItem>
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                                ) : null}

                                                {isAdminActor && !m.is_primary && (
                                                    <button
                                                        onClick={() => handleSetPrimary(m)}
                                                        disabled={updatingMembershipId === m.id}
                                                        className="p-1.5 text-slate-400 hover:text-amber-600 transition-colors disabled:opacity-50"
                                                        title="Set as primary company"
                                                        data-testid={`set-primary-${m.company_id}`}
                                                    >
                                                        <Star className="h-3.5 w-3.5" />
                                                    </button>
                                                )}

                                                {isAdminActor && (
                                                <button
                                                    onClick={() => handleRemoveMembership(m)}
                                                    disabled={removingMembershipId === m.id}
                                                    className="p-1.5 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
                                                    title="Remove from company"
                                                    data-testid={`remove-${m.company_id}`}
                                                >
                                                    {removingMembershipId === m.id ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : (
                                                        <X className="h-3.5 w-3.5" />
                                                    )}
                                                </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* ==================== Notifications Tab ==================== */}
                    <TabsContent value="notifications">
                        <div className="space-y-4">
                            {memberships.length === 0 && (
                                <div className="bg-white rounded-lg border border-border p-6 text-center text-sm text-slate-500">
                                    {t('profile.notif_empty', 'Add this user to a company first to manage their email notifications.')}
                                </div>
                            )}

                            {memberships.map((m) => {
                                const prefs = notifEdits[m.company_id] || {};
                                const dirty = hasUnsavedNotifChanges(m.company_id);
                                return (
                                    <div
                                        key={m.id}
                                        className="bg-white rounded-lg border border-border shadow-premium-sm p-5"
                                        data-testid={`notif-card-${m.company_id}`}
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Building2 className="h-5 w-5 text-slate-400" />
                                                <div>
                                                    <h3 className="text-sm font-semibold text-slate-800">
                                                        {m.company_name}
                                                    </h3>
                                                    <p className="text-xs text-slate-500 capitalize">
                                                        {t('profile.role_label', 'Role:')} {m.role}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleBulkToggle(m.company_id, true)}
                                                    className="h-8 text-xs"
                                                >
                                                    {t('profile.all_on', 'All on')}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleBulkToggle(m.company_id, false)}
                                                    className="h-8 text-xs"
                                                >
                                                    {t('profile.all_off', 'All off')}
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Info banner for mandatory keys — at company level */}
                                        {mandatoryKeys.size > 0 && (
                                            <div
                                                className="mb-3 px-3 py-2 rounded-md bg-indigo-50 border border-indigo-100 text-xs text-indigo-900 flex items-start gap-2"
                                                data-testid={`notif-mandatory-info-${m.company_id}`}
                                            >
                                                <Bell className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                                <span>
                                                    {t('profile.mandatory_info', 'At least one company member must receive artwork approval and revision emails. You can opt out as long as a teammate stays subscribed.')}
                                                </span>
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            {NOTIFICATION_DEFINITIONS.map(({ key, label, help, tKey, helpKey }) => {
                                                const isMandatory = mandatoryKeys.has(key);
                                                const lastRecipient = isLastRecipient(m.company_id, key);
                                                const editedValue = Boolean(prefs[key]);
                                                // Disable when: mandatory, currently checked, and you're the only one
                                                const disableToggle = isMandatory && lastRecipient && editedValue;
                                                return (
                                                    <div
                                                        key={key}
                                                        className="flex items-center justify-between p-2.5 hover:bg-background rounded"
                                                        data-testid={`notif-toggle-${m.company_id}-${key}`}
                                                    >
                                                        <div className="min-w-0 flex-1">
                                                            <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                                                {t(tKey, label)}
                                                                {disableToggle && (
                                                                    <span
                                                                        className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                                                                        data-testid={`notif-last-recipient-badge-${key}`}
                                                                    >
                                                                        {t('profile.notif.last_recipient', 'Last recipient')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-slate-500">
                                                                {t(helpKey, help)}
                                                                {disableToggle && (
                                                                    <span className="ml-1 text-amber-700">
                                                                        {t('profile.notif.only_recipient_warning', "— you're the only one in {company} receiving this. Ask a teammate to enable it before turning yours off.", { company: m.company_name })}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <Switch
                                                            checked={editedValue}
                                                            disabled={disableToggle}
                                                            onCheckedChange={(v) => handleToggleNotif(m.company_id, key, v)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="flex justify-end items-center gap-3 mt-4 pt-3 border-t">
                                            {dirty && (
                                                <span className="text-xs text-amber-600">{t('profile.unsaved_changes', 'Unsaved changes')}</span>
                                            )}
                                            <Button
                                                onClick={() => handleSaveNotifs(m.company_id)}
                                                disabled={!dirty || savingNotifCompanyId === m.company_id}
                                                size="sm"
                                                className="bg-indigo-600 hover:bg-indigo-700"
                                                data-testid={`save-notif-${m.company_id}`}
                                            >
                                                {savingNotifCompanyId === m.company_id ? (
                                                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                                ) : (
                                                    <Save className="h-3.5 w-3.5 mr-1" />
                                                )}
                                                {t('common.save', 'Save')}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </TabsContent>
                </Tabs>
            </main>

            {/* Add Company Dialog */}
            <Dialog open={showAddCompanyDialog} onOpenChange={setShowAddCompanyDialog}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-indigo-600" />
                            Add to Company
                        </DialogTitle>
                        <DialogDescription>
                            Add {profile.name} to a new company with a chosen role.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Search Company</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    placeholder="Search by company name..."
                                    value={companySearchQuery}
                                    onChange={(e) => {
                                        setCompanySearchQuery(e.target.value);
                                        searchCompanies(e.target.value);
                                    }}
                                    className="pl-9"
                                    data-testid="search-company-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Pick Company</Label>
                            {isSearchingCompanies ? (
                                <div className="text-center py-4 text-sm text-slate-400">
                                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                    Searching...
                                </div>
                            ) : companySearchResults.length === 0 ? (
                                <div className="text-center py-4 text-sm text-slate-400 bg-background rounded-lg">
                                    {companySearchQuery ? 'No companies found' : 'Start typing to search'}
                                </div>
                            ) : (
                                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                                    {companySearchResults.map((c) => (
                                        <label
                                            key={c.id}
                                            className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                                                selectedCompanyId === c.id ? 'bg-indigo-50' : 'hover:bg-slate-50'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                checked={selectedCompanyId === c.id}
                                                onChange={() => setSelectedCompanyId(c.id)}
                                                className="h-4 w-4"
                                            />
                                            <span className="text-sm">{c.name}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label>Role</Label>
                            <Select value={selectedMembershipRole} onValueChange={setSelectedMembershipRole}>
                                <SelectTrigger data-testid="add-company-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MEMBERSHIP_ROLES.map((r) => (
                                        <SelectItem
                                            key={r.value}
                                            value={r.value}
                                            disabled={r.value === 'owner' && !isOwnerActor}
                                        >
                                            {r.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                {MEMBERSHIP_ROLES.find((r) => r.value === selectedMembershipRole)?.help}
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddCompanyDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddCompany}
                            disabled={isAddingCompany || !selectedCompanyId}
                            className="bg-indigo-600 hover:bg-indigo-700"
                            data-testid="confirm-add-company-btn"
                        >
                            {isAddingCompany ? 'Adding...' : 'Add'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Profile Picture Dialog — only rendered when viewing own profile */}
            {isCurrentUser && (
                <ProfilePictureDialog
                    open={showPictureDialog}
                    onOpenChange={setShowPictureDialog}
                    user={currentUser}
                    onUpdated={(updatedUser) => {
                        // Push the fresh /me response into AuthContext so
                        // avatars re-render app-wide (Header, Profile).
                        if (updatedUser) setAuthUser(updatedUser);
                        // Also refresh the local profile view — the
                        // profile endpoint may return additional fields
                        // beyond what /me exposes.
                        fetchProfile();
                    }}
                />
            )}
        </div>
    );
}
